/**
 * Ce que les éditeurs fabric (images, vidéo) font de la sélection : la barre d'actions,
 * les sous-pages de la toolbox, et les actions de la barre.
 *
 * FabricControls branche la sélection de l'éditeur sur ces surfaces, et relaie
 * aux contrôleurs (onSelect, onSelectMany, onDeselect, onModified). Elle ne décide pas
 * quels outils montrer : c'est la lib (`toolsFor`), parmi ceux que le serveur a rendus.
 *
 * Le recadrage d'une image dans son cadre (lib : `editor.crop`, un état de l'éditeur,
 * pas une sélection) a sa barre à lui (Réinitialiser · Annuler · Terminé), posée sous le
 * cadre — dedans pour le fond de la page. La barre d'actions s'efface le temps du geste,
 * et tout le recadrage s'enregistre d'un coup à la sortie (un seul ⌘Z).
 *
 * La page (ou la séquence, en vidéo) se sélectionne aussi (option `page`), par son nom — un
 * clic à vide désélectionne, il ne sélectionne jamais le fond. Ce n'est pas une sélection
 * fabric — le fond n'est jamais un élément —, mais un état de l'éditeur. Comme un objet : la
 * barre montre les outils du fond, dans le cadre en haut à gauche, et c'est l'un d'eux qui
 * ouvre sa page.
 *
 * Cliquer un objet (le sélectionner, sans le glisser) ouvre son Apparence, si la toolbox ne
 * montre pas déjà une sous-page de calque — qui, elle, suit la sélection.
 *
 * Usage :
 *   const controls = new FabricControls(editor, controlsEl, {
 *     onModified: () => { ... },
 *     ...editor.getControlsConfig(anchorEl),
 *   });
 *   controls.attach();
 *   controls.destroy();
 */
import { mediumMeta } from "./composition_document";
import { BG_LAYER_ID } from "./fabric_background";
import { conditionStateOf } from "./fabric_bindings";
import { FabricActionBar, type ActionBarState } from "./fabric_action_bar";
import { FabricToolboxPages, type BackgroundTarget } from "./fabric_toolbox_pages";
import {
  FabricEditor,
  ImageFrame,
  getLockMode,
  rulesOf,
  toolsFor,
  type AlignEdge,
  type Arrangement,
  type DistributeAxis,
  type LockMode,
  type ToolId,
} from "@frabr/fabric-editor";
import type { FabricObject } from "fabric";

// eslint-disable-next-line @typescript-eslint/no-explicit-any
type FabricObj = FabricObject & Record<string, any>

interface FabricControlsOpts {
  onModified?: () => void
  onSelect?: (obj: FabricObj) => void
  /** Plusieurs objets sélectionnés : pas de calque unique à montrer dans la toolbox. */
  onSelectMany?: (objects: FabricObj[]) => void
  onDeselect?: () => void
  getContainer: () => HTMLElement
  getDisplayScale?: () => number
  getCanvasOffset?: () => { left: number; top: number }
  positionControls?: (el: HTMLElement, obj: FabricObj) => void
  /** La page du document que le canvas montre (éditeur images) : le fond s'appelle `bg`
   *  sur chaque page, les sous-pages de la toolbox ont besoin de savoir laquelle. */
  getPageId?: () => string | null | undefined
  /** La page (ou la séquence) sélectionnable, et la barre de son fond. */
  page?: PageOpts
}

interface PageOpts {
  /** Les outils de la barre de la page, dans l'ordre, et ce qu'elle montre du fond. */
  bar: () => { tools: string[]; state: ActionBarState }
  /** Ce dont le fond est le fond : la page (slide) ou la séquence, par son id. */
  target: () => BackgroundTarget | null
  /** Où poser sa barre : sur le cadre (défaut), ou en bas du conteneur, centrée — l'éditeur
   *  vidéo, où la page est la séquence : sa barre se pose contre la timeline. */
  dock?: "frame" | "bottom"
  onSelect?: () => void
  onDeselect?: () => void
}

/** Un média de la bibliothèque : sa source, et son id — qui voyage avec elle dans le calque. */
interface ContentMediumRef {
  id?: number | string | null
  url?: string | null
}

const HEX_COLOR = /^#([A-Fa-f0-9]{6}|[A-Fa-f0-9]{3})$/;
/**
 * Ce qu'un clic peut toucher sans désélectionner la page : le canvas (son clic à vide a sa
 * propre règle, onMouseUp), un contrôle (bouton, lien, champ), la barre et ses menus, la
 * toolbox qui règle la page, une modale — et ce qui le déclare (`data-keeps-selection` :
 * une séquence de la timeline, qui sélectionne sa page elle-même).
 */
const KEEPS_PAGE = [
  "[data-fabric='wrapper']", "button", "a[href]", "input", "select", "textarea", "label", "summary",
  "[role='button']", "[contenteditable='true']", "[data-role='layerControls']", "[data-role='cropBar']",
  "[data-floating-menu-target='menu']", "[popover]", "dialog", "#toolbox", "[data-keeps-selection]",
].join(", ");
const TOOLBOX_FRAME_ID = "toolbox-content";
/** Le panneau qu'ouvre le clic sur un objet, et l'écran à partir duquel il s'ouvre seul. */
const DEFAULT_PAGE = "appearance";
const DEFAULT_PAGE_MEDIA = "(min-width: 768px)";
/** La barre de recadrage, sous le cadre (ou dedans, en bas, faute de place) : son écart. */
const CROP_BAR_GAP = 12;
const CROP_BAR_INSET = 16;
const EDGE = 8;

export class FabricControls {
  editor: FabricEditor
  controlsEl: HTMLElement
  onModified: () => void

  private readonly bar: FabricActionBar
  private readonly pages: FabricToolboxPages
  private readonly cropBar: HTMLElement | null
  private readonly callbacks: Pick<FabricControlsOpts, "onSelect" | "onSelectMany" | "onDeselect">
  private readonly getContainer: () => HTMLElement
  private readonly getDisplayScale: () => number
  private readonly getCanvasOffset: () => { left: number; top: number }
  private readonly page: PageOpts | null
  private pageSelected = false
  /** L'objet sélectionné quand le bouton s'enfonce : au relâcher, un clic qui en a
   *  sélectionné un autre ouvre son Apparence. */
  private selectedAtDown: FabricObject | null = null
  private toolbarObserver: ResizeObserver | null = null
  // En édition d'un texte, la barre reste et se réduit à ce qui se règle sur une plage
  // (toolsFor) ; à la sortie, elle reprend tous les outils du texte
  private readonly onEditingToggled = () => this.render();
  /** Le recadrage : la barre d'actions s'efface, la sienne se pose ; à la sortie, un seul
   *  enregistrement si quelque chose a changé, et la barre d'actions revient. */
  private readonly onCropStart = () => {
    this.hide();
    this.showCropBar();
  };
  private readonly onCropEnd = (e: { changed?: boolean }) => {
    this.hideCropBar();
    if (e.changed) this.onModified();
    this.render();
  };
  private readonly onMouseDownBefore = () => {
    this.selectedAtDown = this.editor.selection.current;
  };
  /** Un clic (pas un glisser, ni un lasso) : sur un objet qu'il vient de sélectionner, son
   *  Apparence ; à vide, plus rien de sélectionné — ni objet (fabric), ni page. */
  private readonly onMouseUp = (e: { target?: unknown; isClick?: boolean }) => {
    if (!e.isClick) return;
    if (!e.target) {
      this.deselectPage();
      return;
    }
    const { current, selected } = this.editor.selection;
    if (current && selected.length === 1 && current !== this.selectedAtDown) this.openDefaultPage(current);
  };
  private readonly onKeyDown = (e: KeyboardEvent) => {
    if (e.key === "Escape" && this.pageSelected) this.deselectPage();
  };
  /** Tout clic à côté de la barre et des contrôles désélectionne la page (KEEPS_PAGE). */
  private readonly onPointerDownOutside = (e: PointerEvent) => {
    if (!this.pageSelected || this.editor.crop.active) return;
    const target = e.target as Element | null;
    if (!target?.isConnected || target.closest(KEEPS_PAGE)) return;
    this.deselectPage();
  };
  /** La toolbox a chargé une page : l'outil ouvert est le sien. */
  private readonly onFrameLoad = (e: Event) => {
    if ((e.target as HTMLElement).id === TOOLBOX_FRAME_ID) this.bar.markOpen(this.pages.openPage());
  };

  constructor(editor: FabricEditor, controlsEl: HTMLElement, opts: FabricControlsOpts) {
    this.editor = editor;
    this.controlsEl = controlsEl;
    this.onModified = opts.onModified || (() => {});
    this.callbacks = { onSelect: opts.onSelect, onSelectMany: opts.onSelectMany, onDeselect: opts.onDeselect };
    this.getContainer = opts.getContainer;
    this.page = opts.page || null;

    const getDisplayScale = opts.getDisplayScale || (() => 1);
    this.getDisplayScale = getDisplayScale;
    this.getCanvasOffset = opts.getCanvasOffset || (() => ({ left: 0, top: 0 }));
    this.bar = new FabricActionBar(controlsEl, {
      getContainer: opts.getContainer,
      getDisplayScale,
      getCanvasOffset: this.getCanvasOffset,
      position: opts.positionControls as ((el: HTMLElement, obj: FabricObject) => void) | undefined,
      shortcutFor: (tool) => this.editor.shortcuts?.labelForTool(tool) ?? null,
    });
    this.pages = new FabricToolboxPages(controlsEl.dataset.overlayBaseUrl, opts.getPageId,
                                        JSON.parse(controlsEl.dataset.pagePanelUrls || "{}"));

    // La barre de recadrage vit hors de la barre d'actions, dans le même conteneur
    this.cropBar = opts.getContainer()?.querySelector<HTMLElement>("[data-role='cropBar']") ?? null;
  }

  attach(): void {
    const { selection } = this.editor;

    // Un objet sélectionné rend la sélection de la page
    selection.onSelect = (obj: FabricObj) => {
      this.deselectPage();
      // L'outil marqué ouvert est celui que la toolbox montre maintenant : revenue à sa home
      // entre-temps (désélection), elle n'en montre aucun
      this.bar.markOpen(this.pages.openPage());
      this.render();
      // Une sous-page ouverte suit l'objet ; Apparence, elle, attend le relâcher (onMouseUp) :
      // glisser un objet n'ouvre rien
      this.pages.follow(obj.get("layerId"));
      this.callbacks.onSelect?.(obj);
    };
    selection.onSelectMany = (objects: FabricObj[]) => {
      this.deselectPage();
      this.render();
      this.callbacks.onSelectMany?.(objects);
      // Ce que l'éditeur vient de rendre possible (bulles « première fois », Fabric::ActionBarComponent)
      window.dispatchEvent(new CustomEvent("editor:multi-select"));
    };
    selection.onDeselect = () => {
      this.render();
      this.callbacks.onDeselect?.();
    };
    // Pendant un geste, la barre s'efface
    selection.onTransformStart = () => this.hide();
    this.editor.canvas.on("text:editing:entered", this.onEditingToggled);
    this.editor.canvas.on("text:editing:exited", this.onEditingToggled);
    this.editor.canvas.on("crop:start", this.onCropStart);
    this.editor.canvas.on("crop:end", this.onCropEnd);
    // Un objet modifié peut avoir changé d'état (devenu container, verrouillé…) : la barre
    // se recalcule
    selection.onModified = () => {
      this.onModified();
      this.render();
    };

    this.editor.canvas.on("mouse:down:before", this.onMouseDownBefore);
    this.editor.canvas.on("mouse:up", this.onMouseUp);
    if (this.page) {
      document.addEventListener("keydown", this.onKeyDown);
      document.addEventListener("pointerdown", this.onPointerDownOutside);
    }

    this.controlsEl.addEventListener("click", (e) => this.onPageClick(e));
    document.addEventListener("turbo:frame-load", this.onFrameLoad);
    this.bar.markOpen(this.pages.openPage());
    this.render();

    // La barre se centre sur sa largeur : quoi qui la change (outils, polices chargées
    // après coup), elle se recentre
    if (typeof ResizeObserver !== "undefined") {
      this.toolbarObserver = new ResizeObserver(() => this.repositionActive());
      this.toolbarObserver.observe(this.controlsEl);
    }
  }

  destroy(): void {
    const { selection } = this.editor;
    selection.onSelect = undefined;
    selection.onSelectMany = undefined;
    selection.onDeselect = undefined;
    selection.onTransformStart = undefined;
    selection.onModified = undefined;
    this.editor.canvas.off("text:editing:entered", this.onEditingToggled);
    this.editor.canvas.off("text:editing:exited", this.onEditingToggled);
    this.editor.canvas.off("crop:start", this.onCropStart);
    this.editor.canvas.off("crop:end", this.onCropEnd);
    this.editor.canvas.off("mouse:down:before", this.onMouseDownBefore);
    this.editor.canvas.off("mouse:up", this.onMouseUp);
    document.removeEventListener("keydown", this.onKeyDown);
    document.removeEventListener("pointerdown", this.onPointerDownOutside);
    document.removeEventListener("turbo:frame-load", this.onFrameLoad);
    this.bar.destroy();
    this.toolbarObserver?.disconnect();
    this.toolbarObserver = null;
  }

  /** La barre se rejoue sur la sélection courante : ce qu'elle lit de l'objet a changé sans
   *  que la sélection bouge (une condition posée ou levée, son fantôme). */
  refreshSelection(): void {
    this.render();
  }

  /** La barre se replace sur la sélection (le canvas a changé de taille…). */
  repositionActive(): void {
    if (this.editor.crop.active) {
      this.placeCropBar();
      return;
    }
    if (this.pageSelected) {
      this.placePageBar();
      return;
    }
    const active = this.editor.canvas.getActiveObject() as FabricObj | null;
    if (!active || !this.bar.visible) return;
    this.bar.place(active);
  }

  // ==================== Recadrage ====================

  /** Recadrer l'image sélectionnée dans son cadre (« Recadrer », ou double-clic). */
  cropSelection(): void {
    const obj = this.editor.selection.current;
    if (obj instanceof ImageFrame && rulesOf(obj).options.includes("image")) this.editor.crop.start(obj);
  }

  /** Recadrer le fond image de la page : le cadre est la page. */
  cropBackground(): void {
    const bg = this.editor.layers.all.find((obj) => obj.get("layerId") === BG_LAYER_ID);
    if (bg instanceof ImageFrame && rulesOf(bg).options.includes("image")) this.editor.crop.start(bg);
  }

  cropReset(): void { this.editor.crop.reset(); }
  cropCancel(): void { this.editor.crop.cancel(); }
  cropDone(): void { this.editor.crop.commit(); }

  private showCropBar(): void {
    if (!this.cropBar) return;
    this.cropBar.hidden = false;
    this.placeCropBar();
  }

  private hideCropBar(): void {
    if (this.cropBar) this.cropBar.hidden = true;
  }

  /** Centrée sous le cadre ; dedans, en bas, pour le fond (le cadre est la page) ou faute
   *  de place en dessous. */
  private placeCropBar(): void {
    // L'ImageFrame de la lib n'est pas typé comme un FabricObject (extension de Group)
    const frame = this.editor.crop.target as unknown as FabricObject | null;
    const container = this.getContainer();
    if (!this.cropBar || !frame || !container) return;

    const rect = this.bar.displayRect(frame);
    const width = this.cropBar.offsetWidth || 240;
    const height = this.cropBar.offsetHeight || 64;
    const below = rect.top + rect.height + CROP_BAR_GAP + height;
    const inside = !frame.selectable || below > container.clientHeight - EDGE;
    const top = inside ? rect.top + rect.height - CROP_BAR_INSET - height : rect.top + rect.height + CROP_BAR_GAP;
    const left = Math.max(EDGE, Math.min(rect.left + rect.width / 2 - width / 2, container.clientWidth - width - EDGE));
    this.cropBar.dataset.placement = inside ? "inside" : "below";
    Object.assign(this.cropBar.style, { left: `${left}px`, top: `${Math.max(EDGE, top)}px` });
  }

  // ==================== La page ====================

  get isPageSelected(): boolean {
    return this.pageSelected;
  }

  /**
   * Sélectionne la page : plus d'objet sélectionné, la barre du fond. `panel` (`color`,
   * `media`) ouvre aussi cette page du fond dans la toolbox — l'invitation d'une page
   * vierge, dont c'est l'action même.
   */
  selectPage(panel?: string): void {
    if (!this.page) return;
    if (this.editor.canvas.getActiveObject()) this.editor.selection.clear();

    this.pageSelected = true;
    this.render();
    this.page.onSelect?.();

    const target = this.page.target();
    const url = panel && target && this.pages.backgroundUrl(target, panel);
    if (url) window.dispatchEvent(new CustomEvent("toolbox:show", { detail: { key: "background", url } }));
  }

  /** La page sélectionnée se désélectionne, et inversement (le nom de la page). */
  togglePage(): void {
    if (this.pageSelected) this.deselectPage();
    else this.selectPage();
  }

  /** La page n'est plus sélectionnée : la page du fond ouverte se ferme avec elle. */
  deselectPage(): void {
    if (!this.pageSelected) return;

    this.pageSelected = false;
    this.render();
    if (this.pages.isBackground(this.pages.openPage())) {
      window.dispatchEvent(new CustomEvent("toolbox:show", { detail: { key: "home" } }));
    }
    this.page?.onDeselect?.();
  }

  /** Le fond de la page a changé : sa barre aussi. */
  refreshPage(): void {
    if (this.pageSelected) this.render();
  }

  private placePageBar(): void {
    if (this.page?.dock === "bottom") this.bar.placeAtBottom();
    else this.bar.placeInFrame(this.frameRect());
  }

  /** Le cadre du document, en pixels du conteneur. */
  private frameRect(): { left: number; top: number; width: number; height: number } {
    const scale = this.getDisplayScale();
    const { left, top } = this.getCanvasOffset();
    return { left, top, width: this.editor.width * scale, height: this.editor.height * scale };
  }

  // ==================== Sous-pages de la toolbox ====================

  /** La toolbox montre la sous-page d'un calque (pas la bibliothèque, ni la page Fond) : sans
   *  objet sélectionné, elle n'a plus de sujet. */
  layerPageOpen(): boolean {
    const page = this.pages.openPage();
    return Boolean(page && !this.pages.isBackground(page));
  }

  /** L'url d'une sous-page pour un calque. */
  overlayPageUrl(page: string, layerId: string): string | null {
    return this.pages.urlFor(page, layerId);
  }

  /** Apparence, sauf si une sous-page de calque est déjà ouverte (elle suit l'objet), sur petit
   *  écran (la toolbox couvrirait le document), ou pour un objet qui n'en a pas (verrouillé). */
  private openDefaultPage(obj: FabricObject): void {
    if (this.layerPageOpen()) return;
    if (!window.matchMedia?.(DEFAULT_PAGE_MEDIA).matches) return;
    if (!toolsFor([obj], this.bar.offered as ToolId[]).includes(DEFAULT_PAGE)) return;

    const url = this.pages.urlFor(DEFAULT_PAGE, obj.get("layerId"));
    if (!url) return;
    window.dispatchEvent(new CustomEvent("toolbox:show", { detail: { key: "overlay", url } }));
    this.bar.markOpen(DEFAULT_PAGE);
  }

  /** Le document vient d'être sauvegardé : la sous-page ouverte montre ce qu'il dit. */
  reloadOverlayPanel(): void {
    this.pages.reload();
  }

  // ==================== Actions ====================

  // Supprimer, grouper, dégrouper : la lib prévient l'hôte elle-même (selection.onModified),
  // comme pour ses raccourcis — pas de second rappel ici.
  deleteSelection(): void {
    this.editor.deleteSelection();
    this.hide();
  }

  toggleOutline(): void {
    if (!this.restylable()) return;
    this.editor.toggleOutline();
    this.onModified();
  }

  changeColor(color: string): void {
    if (!this.restylable() || !HEX_COLOR.test(color || "")) return;
    this.editor.changeColor(color);
    this.onModified();
  }

  changeOpacity(opacity: number): void {
    if (!this.restylable()) return;
    this.editor.changeOpacity(opacity);
  }

  changeFont(fontFamily: string, fontWeight?: string): void {
    if (!this.restylable() || !fontFamily) return;
    this.editor.changeFont(fontFamily, fontWeight);
    this.onModified();
  }

  bringForward(): void {
    const objects = this.restackable();
    if (!objects.length) return;
    this.editor.layers.bringForward(objects);
    this.onModified();
  }

  sendBackward(): void {
    const objects = this.restackable();
    if (!objects.length) return;
    this.editor.layers.sendBackward(objects);
    this.onModified();
  }

  group(): void {
    if (!this.editor.groupSelection()) return;
    window.dispatchEvent(new CustomEvent("editor:group-created"));
  }

  ungroup(): void {
    this.editor.ungroupSelection();
  }

  setArrangement(arrangement: Arrangement): void {
    const obj = this.restylable();
    if (!obj) return;
    this.editor.layout.setArrangement(obj, arrangement);
    this.onModified();
    this.render();
  }

  align(edge: AlignEdge): void {
    this.editor.alignSelection(edge);
    this.onModified();
  }

  distribute(axis: DistributeAxis): void {
    this.editor.distributeSelection(axis);
    this.onModified();
  }

  /** Le verrou de l'objet, au mode choisi dans son menu. */
  setLockMode(mode: LockMode): void {
    const obj = this.editor.selection.current;
    if (!obj || getLockMode(obj) === mode) return;
    this.editor.layers.applyLockMode(obj, mode);
    this.editor.canvas.renderAll();
    this.render();
    this.onModified();
  }

  /** Une forme ou une image simple devient un bloc : elle accueille, même vide. */
  containerize(): void {
    const obj = this.restylable();
    if (!obj) return;
    this.editor.layout.makeContainer(obj);
    this.onModified();
    this.render();
  }

  addShape(opts: Record<string, unknown> = {}) {
    return this.editor.layers.addShape(opts);
  }

  /** Un texte posé depuis la toolbox est un exemple (placeholder) : la première variable
   *  insérée le remplace au lieu de s'y ajouter. */
  addText(opts: Record<string, unknown> = {}) {
    return this.editor.layers.addText({
      text: "Texte",
      fontFamily: "Inter",
      fontSize: 32,
      fill: "#000000",
      placeholder: true,
      ...opts,
    });
  }

  async addImage(cm: ContentMediumRef) {
    if (!cm.url) return null;
    return await this.editor.layers.addImage(cm.url, { imageMeta: mediumMeta(cm) });
  }

  async replaceSelectedImage(cm: ContentMediumRef): Promise<void> {
    const obj = this.editor.selection.current as FabricObj | null;
    if (obj) await this.replaceImage(obj, cm);
  }

  /** L'image d'un calque qui en prend une — la sélection, ou la cible figée d'un upload. */
  async replaceImage(obj: FabricObj, cm: ContentMediumRef): Promise<void> {
    if (!cm.url || !rulesOf(obj).options.includes("image")) return;
    await this.editor.layers.replaceImageSource(obj, cm.url, { imageMeta: mediumMeta(cm) });
    this.onModified();
  }

  // ==================== Rendu ====================

  /**
   * La barre de la sélection courante, en un seul passage : les outils que la lib donne,
   * l'état de l'objet, les liens des sous-pages, puis la mise en place — en dernier, la
   * barre se centrant sur sa largeur. Seul point d'entrée : aucun ordre d'appel à tenir.
   */
  private render(): void {
    if (this.editor.crop.active) {
      this.hide();
      return;
    }
    if (this.pageSelected && this.page) {
      const { tools, state } = this.page.bar();
      this.bar.show(tools.filter((tool) => this.bar.offered.includes(tool)), state);
      const target = this.page.target();
      if (target) this.pages.linkBackground(this.controlsEl, target);
      this.placePageBar();
      return;
    }

    const { selection } = this.editor;
    const active = this.editor.canvas.getActiveObject() as FabricObj | null;
    const selected = selection.selected as FabricObj[];
    if (!active || selected.length === 0) {
      this.hide();
      return;
    }

    const one = selected.length === 1 ? selected[0] : null;
    const container = one?.get("layout")?.container;
    this.bar.show(toolsFor(selected, this.bar.offered as ToolId[]), {
      lockMode: one ? getLockMode(one) : undefined,
      arrangement: container?.arrangement,
      direction: container?.flexDirection,
      color: typeof one?.fill === "string" ? one.fill : null,
      condition: one ? conditionStateOf(one) : undefined,
    });
    if (one) this.pages.link(this.controlsEl, one.get("layerId"));

    this.bar.place(active);
  }

  /**
   * Un outil à sous-page : il l'ouvre, et la marque ouverte sans attendre la toolbox. Sa
   * sous-page déjà ouverte, il la ferme — la toolbox revient à sa racine, l'objet reste
   * sélectionné.
   */
  private onPageClick(e: MouseEvent): void {
    const link = (e.target as HTMLElement).closest<HTMLElement>("[data-page]");
    const page = link?.dataset.page;
    if (!page) return;

    if (this.pages.openPage() === page) {
      e.preventDefault();
      window.dispatchEvent(new CustomEvent("toolbox:show", { detail: { key: "overlay", root: true } }));
      this.bar.markOpen(null);
    } else {
      this.bar.markOpen(page);
    }
  }

  private hide(): void {
    this.bar.hide();
  }

  /** L'objet sélectionné, s'il peut changer de style. */
  private restylable(): FabricObj | null {
    const obj = this.editor.selection.current as FabricObj | null;
    return obj && rulesOf(obj).restyles ? obj : null;
  }

  /** Les objets sélectionnés qui changent de plan : tous, ou aucun. */
  private restackable(): FabricObject[] {
    const objects = this.editor.selection.selected;
    return objects.every((obj) => rulesOf(obj).restacks) ? objects : [];
  }

}
